/**
 * CalmEmptyState — universal empty-state surface (Feb 13, 2026).
 *
 * Operator audit rule (verbatim):
 *   "Do not leave broken-looking 0/0 or 'No cards in this filter'
 *    states on active learner pages. Empty states must look
 *    intentional, not broken."
 *
 * Use this anywhere a list/grid/deck could legitimately be empty.
 * Two modes:
 *
 *   tone="nothing-yet"  (default)
 *     - "Nothing here yet" / similar friendly copy
 *     - Calm slate palette
 *     - For active surfaces that just haven't filled up
 *
 *   tone="coming-soon"
 *     - "Coming soon" indigo accent
 *     - For aspirational signposts the learner should see exists
 *       even before content lands
 *
 * Always renders a dashed-bordered card so it visually reads as
 * "intentional space" rather than "broken layout". Optional CTA
 * for cases where the user can DO something to fix the empty
 * state (e.g. clear a filter).
 */
import { Sparkles, Compass, RefreshCw } from "lucide-react";


const TONES = {
  "nothing-yet": {
    Icon: Compass,
    classes: "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200",
    iconTone: "text-slate-400",
    titleTone: "text-slate-700 dark:text-slate-200",
    bodyTone: "text-slate-500 dark:text-slate-400",
  },
  "coming-soon": {
    Icon: Sparkles,
    classes: "bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-100",
    iconTone: "text-indigo-500",
    titleTone: "text-indigo-900 dark:text-indigo-100",
    bodyTone: "text-indigo-700/80 dark:text-indigo-200/70",
  },
};


export default function CalmEmptyState({
  title,
  body,
  tone = "nothing-yet",
  cta,
  ctaLabel,
  testid = "calm-empty-state",
}) {
  const t = TONES[tone] || TONES["nothing-yet"];
  const { Icon } = t;
  return (
    <div
      data-testid={testid}
      data-tone={tone}
      className={`rounded-2xl border border-dashed ${t.classes} p-6 text-center`}
    >
      <div className="flex justify-center mb-2.5">
        <Icon size={20} className={t.iconTone} />
      </div>
      <p className={`text-sm font-bold ${t.titleTone}`}>
        {title}
      </p>
      {body && (
        <p className={`text-[12px] mt-1.5 leading-relaxed ${t.bodyTone}`}>
          {body}
        </p>
      )}
      {cta && (
        <button
          type="button"
          onClick={cta}
          data-testid={`${testid}-cta`}
          className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold text-indigo-700 dark:text-indigo-300 hover:underline"
        >
          <RefreshCw size={12} />
          {ctaLabel || "Try again"}
        </button>
      )}
    </div>
  );
}
